import React, { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import _ from 'lodash';
import { getCoreRowModel } from '@tanstack/react-table';

// MUL Theme
import { Box, Button, MenuItem, Paper, Select } from '@mui/material';

// Services
import { exportEquipment, getEquipment, updateEquipmentLabels } from '../../../services/equipment.service';

// Utils
import { Storage, getRetainState } from '../../../utils/Storage';
import { makeEquipmentListColumn } from '../../../utils/columns/makeColumn';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../redux/hook';
import { useNotifyMessage } from '../../../hooks/useNotifyMessage';
import { useExport } from '../../../hooks/useExport';

// Redux
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';

// Types
import { SearchBarFilterTypes } from '../../../constants/Constants';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { ResourceType } from '../../../types/ResourceType';

// Components
import { useStandardTable as useTable, Table } from '../../common/table/index';
import SearchBar from '../../common/SearchBar/SearchBar';
import CustomModal from '../../common/withModal';
import LabelAssignment from '../labels/labelAssignment';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

interface EquipmentProp {
  accessKey?: string;
}

export default function Equipment(props: EquipmentProp): JSX.Element {
  const { notifyMessage } = useNotifyMessage();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedLabelItem, setSelectedLabelItem] = useState<any>(null);
  const dispatch = useAppDispatch();
  const [paramFilter, setParamFilter] = useState<any>({
    entityType: 'Equipment',

    sortBy: [
      {
        serialNumber: '1',
      },
    ],
  });
  const [isResetting, setIsResetting] = useState(false);
  const [uldStatus, setUldStatus] = useState<number | undefined>();
  const [equipmentType, setEquipmentType] = useState<number | undefined>();

  const settingStates = useAppSelector((state) => state.settings);

  const columns = useMemo(() => makeEquipmentListColumn(), []);

  const { isExporting, startExport } = useExport('Equipment');

  const table = useTable<any>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getEquipment,
    exportAsync: exportEquipment,
    params: paramFilter,
    serverSide: true,
  });

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.EQUIPMENTS);
    getRetainState(SearchBarFilterTypes.EQUIPMENTS, 'status', setUldStatus);
    getRetainState(SearchBarFilterTypes.EQUIPMENTS, 'type', setEquipmentType);

    if (filterState) {
      setParamFilter({
        entityType: 'Equipment',
        filters: filterState.filters,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
    intialDefaultFilters();
  }, []);

  useEffect(() => {
    if (isResetting) {
      // Reset paramFilter and clear the resetting flag
      setParamFilter({
        entityType: 'Equipment',
        filters: [],
        sortBy: [
          {
            serialNumber: '1',
          },
        ],
      });
      setIsResetting(false);
    }
  }, [isResetting, paramFilter.sortBy]);

  const onSearchChange = (searchFilters: any[] = []) => {
    const filters: any[] = [...searchFilters];

    if (uldStatus != null) {
      filters.push({
        fieldName: 'status',
        fieldValue: Number(uldStatus),
        filterType: FilterableTypes.Equals,
      });
    }

    if (equipmentType != null) {
      filters.push({
        fieldName: 'type',
        fieldValue: Number(equipmentType),
        filterType: FilterableTypes.Equals,
      });
    }

    setParamFilter({ entityType: 'Equipment', filters });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.EQUIPMENTS, { isMoreFilter: true, filters });
  };

  const handleReset = () => {
    setUldStatus(undefined);
    setEquipmentType(undefined);
    setIsResetting(true);
    setParamFilter({ entityType: 'Equipment', filters: [], sortBy: [{ serialNumber: '1' }] });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.EQUIPMENTS, { filters: [] });
  };

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => handleSubmitForm(formik),
  });

  const handleSubmitForm = async (formik) => {
    await updateEquipmentLabels({
      entityIds: selectedLabelItem.entityIds,
      labelIds: formik.getFieldProps('labelIds')?.value ?? [],
    }).then((result: any) => {
      if (result.status === 200) {
        setIsEditDialogOpen(false);
        setParamFilter({ ...paramFilter, changedAt: new Date() });
        notifyMessage('Label updated', 'success');
        //reset formik
        formik.setFieldValue('labelIds', []);
        return;
      }
      notifyMessage('An error occurred', 'error');
    });
  };

  return (
    <React.Fragment>
      <SearchBar
        pageKey={SearchBarFilterTypes.EQUIPMENTS}
        onSearch={onSearchChange}
        onReset={handleReset}
        allowSearchAll={true}
        columns={columns}
        components={
          <div style={{ display: 'flex', marginBottom: 5 }}>
            <FormInputCollection label='Equipment Status' style={{ width: '140px' }}>
              <Select
                value={uldStatus ?? 'all'}
                variant='outlined'
                id='status'
                name='status'
                size='small'
                style={{ width: '140px' }}
                onChange={(e: any) => {
                  setUldStatus(e.target.value === 'all' ? undefined : e.target.value);
                }}
              >
                <MenuItem value={'all'}>All</MenuItem>
                {settingStates.uldStatus?.value.map((val, index: number) => {
                  return (
                    <MenuItem key={index} value={val.text}>
                      {val.value}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormInputCollection>
            <FormInputCollection label='Equipment Type' style={{ marginLeft: '24px', width: '140px' }}>
              <Select
                value={equipmentType ?? 'all'}
                variant='outlined'
                id='type'
                name='type'
                size='small'
                style={{ width: '140px' }}
                onChange={(e: any) => {
                  setEquipmentType(e.target.value === 'all' ? undefined : e.target.value);
                }}
              >
                <MenuItem value={'all'}>All</MenuItem>
                {settingStates.equipmentTypes?.value.map((val, index: number) => {
                  return (
                    <MenuItem key={index} value={val.text}>
                      {val.value}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormInputCollection>
          </div>
        }
      />
      <Paper>
        <CustomModal
          title='Labels'
          isOpen={isEditDialogOpen}
          isValid={formik.isValid}
          setIsOpen={setIsEditDialogOpen}
          onConfirm={formik.handleSubmit}
          maxWidth='xs'
        >
          <LabelAssignment formRef={formik} resourceType={ResourceType.Equipment} selectedItem={selectedLabelItem} />
        </CustomModal>
        <div>
          <Table
            {...table}
            isExporting={isExporting}
            canExportData={() => true}
            exportData={() => startExport(paramFilter.filters ?? [])}
            showSearchBar={false}
            showColumnToggler={false}
            showAction={true}
            getCoreRowModel={getCoreRowModel()}
            renderHeaderAction={({ selectedFlatRows }) => {
              return selectedFlatRows.length > 0 ? (
                <div style={{ textAlign: 'right' }}>
                  <Box display='flex' justifyContent='flex-end'>
                    <Button
                      variant='contained'
                      color={'error'}
                      size='small'
                      disabled={isEditDialogOpen}
                      style={{ textTransform: 'none' }}
                      onClick={() => {
                        const labelIds: string[] = [];
                        selectedFlatRows.forEach((item) => {
                          if (item.original.labelTypeIds.length > 0) {
                            item.original.labelTypeIds.forEach((v) => labelIds.push(v));
                          }
                        });

                        const uniqLabelIds = _.uniq(labelIds);
                        setSelectedLabelItem({
                          entityIds: selectedFlatRows.map((x) => x.original.id),
                          labelIds: uniqLabelIds,
                        });
                        formik.setFieldValue('labelIds', uniqLabelIds);
                        setIsEditDialogOpen(true);
                      }}
                    >
                      Assign Labels to Equipment
                    </Button>
                  </Box>
                </div>
              ) : null;
            }}
          />
        </div>
      </Paper>
    </React.Fragment>
  );
}
